// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Additional terms under AGPL section 7(b) apply — see ADDITIONAL-TERMS.md.

/**
 * Crockford base32. No I, L, O or U — so a project key read aloud over a call, or typed off a
 * sticky note, survives the O/0 and I/1 confusions that make base32 unusable for humans.
 */
export const CROCKFORD_ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';

const DECODE_MAP: Readonly<Record<string, number>> = (() => {
  const m: Record<string, number> = {};
  for (let i = 0; i < CROCKFORD_ALPHABET.length; i++) {
    const c = CROCKFORD_ALPHABET[i]!;
    m[c] = i;
    m[c.toLowerCase()] = i;
  }
  // Documented folds. These are why the checksum cannot claim to reject 100% of typos: a fold
  // decodes to the identical key by design, so it is not a corruption at all.
  m['O'] = 0; m['o'] = 0;
  m['I'] = 1; m['i'] = 1;
  m['L'] = 1; m['l'] = 1;
  return Object.freeze(m);
})();

/** Big-endian bit packing. 20 bytes = 160 bits = exactly 32 characters, no padding. */
export function encodeCrockford(bytes: Uint8Array): string {
  let out = '';
  let acc = 0;
  let bits = 0;
  for (const b of bytes) {
    acc = (acc << 8) | b;
    bits += 8;
    while (bits >= 5) {
      out += CROCKFORD_ALPHABET[(acc >>> (bits - 5)) & 31];
      bits -= 5;
    }
  }
  if (bits > 0) out += CROCKFORD_ALPHABET[(acc << (5 - bits)) & 31];
  return out;
}

export function decodeCrockford(text: string): Uint8Array {
  const clean = text.replace(/[\s-]/g, '');
  const out: number[] = [];
  let acc = 0;
  let bits = 0;
  for (const ch of clean) {
    const v = DECODE_MAP[ch];
    if (v === undefined) throw new Error(`invalid character "${ch}" in key`);
    acc = (acc << 5) | v;
    bits += 5;
    if (bits >= 8) {
      out.push((acc >>> (bits - 8)) & 0xff);
      bits -= 8;
    }
  }
  return new Uint8Array(out);
}

/** Pack an integer into `chars` Crockford characters, most significant first. */
export function crockfordFromBits(value: number, chars: number): string {
  let out = '';
  for (let i = chars - 1; i >= 0; i--) {
    const shift = i * 5;
    const idx = Math.floor(value / Math.pow(2, shift)) % 32;
    out += CROCKFORD_ALPHABET[idx];
  }
  return out;
}

export function toHex(bytes: Uint8Array): string {
  let s = '';
  for (const b of bytes) s += b.toString(16).padStart(2, '0');
  return s;
}
